import { useState, FormEvent, ChangeEvent } from 'react';
import { trackConversion } from '@/utils/gtag';

const FORM_URL = 'https://readdy.ai/api/form/d75kfnlr5g02gnd69ig0';

export default function QuoteForm() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError(false);
    const formData = new FormData(e.currentTarget);
    const body = new URLSearchParams(
      Array.from(formData.entries()).map(([k, v]) => [k, String(v)])
    ).toString();
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (res.ok) {
        setSubmitted(true);
        trackConversion('QUOTE_FORM_SUBMIT');
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 font-heading focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy/20 transition bg-white placeholder-gray-400';
  const labelCls = 'block text-xs font-semibold font-heading text-navy mb-1.5 uppercase tracking-wide';

  if (submitted) {
    return (
      <div className="text-center py-12 px-6">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-brand-green/10 mx-auto mb-4">
          <i className="ri-checkbox-circle-fill text-4xl text-brand-green" />
        </div>
        <h3 className="font-heading font-black text-navy text-xl mb-2">Quote Request Received!</h3>
        <p className="font-arabic text-gray-500 text-base mb-3" dir="rtl">تم استلام طلب عرض السعر!</p>
        <p className="text-gray-500 text-sm max-w-xs mx-auto">
          Our team will respond within <strong className="text-navy">24 hours</strong>. For faster response, WhatsApp us directly.
        </p>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls} htmlFor="full_name">Full Name</label>
          <input id="full_name" name="full_name" type="text" required placeholder="Your name" className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="company_name">Company Name</label>
          <input id="company_name" name="company_name" type="text" required placeholder="Company or organisation" className={inputCls} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls} htmlFor="business_type">Business Type</label>
          <select id="business_type" name="business_type" required className={inputCls}>
            <option value="">Select type…</option>
            <option>Hospital / Clinic</option>
            <option>Restaurant / Hotel</option>
            <option>Food Processing</option>
            <option>Laboratory</option>
            <option>Factory / Industrial</option>
            <option>Government / Military</option>
            <option>Pharmacy</option>
            <option>Distributor / Wholesaler</option>
            <option>Other</option>
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="location">Location</label>
          <select id="location" name="location" required className={inputCls}>
            <option value="">Select country…</option>
            <option>Oman</option>
            <option>UAE</option>
            <option>Saudi Arabia</option>
            <option>Kuwait</option>
            <option>Bahrain</option>
            <option>Qatar</option>
            <option>Other</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls} htmlFor="email">Email Address</label>
          <input id="email" name="email" type="email" required placeholder="you@company.com" className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="whatsapp_number">WhatsApp Number</label>
          <input id="whatsapp_number" name="whatsapp_number" type="tel" required placeholder="+968 XXXX XXXX" className={inputCls} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls} htmlFor="product_type">Product Type</label>
          <select id="product_type" name="product_type" required className={inputCls}>
            <option value="">Select product…</option>
            <option>Medical / Examination Gloves</option>
            <option>Food Grade Gloves</option>
            <option>Laboratory Gloves</option>
            <option>Industrial Gloves</option>
            <option>Mixed / Multiple Products</option>
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="quantity">Quantity Required</label>
          <input id="quantity" name="quantity" type="text" required placeholder="e.g. 5,000 boxes" className={inputCls} />
        </div>
      </div>

      <div>
        <label className={labelCls} htmlFor="order_frequency">Order Frequency</label>
        <select id="order_frequency" name="order_frequency" required className={inputCls}>
          <option value="">Select frequency…</option>
          <option>One-time Order</option>
          <option>Monthly</option>
          <option>Quarterly</option>
          <option>Bi-annual</option>
          <option>Annual Contract</option>
        </select>
      </div>

      <div>
        <label className={labelCls} htmlFor="additional_notes">
          Additional Notes <span className="normal-case font-normal text-gray-400">({charCount}/500)</span>
        </label>
        <textarea
          id="additional_notes"
          name="additional_notes"
          rows={3}
          maxLength={500}
          placeholder="Any specific requirements, sizes, certifications needed…"
          className={`${inputCls} resize-none`}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setCharCount(e.target.value.length)}
        />
        {charCount >= 500 && (
          <p className="text-red-500 text-xs mt-1">Maximum 500 characters reached.</p>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">
          Something went wrong. Please try again or contact us via WhatsApp.
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || charCount > 500}
        className="w-full bg-navy text-white font-heading font-bold py-3.5 rounded-full hover:bg-navy/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
      >
        {submitting ? (
          <><i className="ri-loader-4-line animate-spin" /> Submitting…</>
        ) : (
          <><i className="ri-send-plane-fill" /> Submit Quote Request</>
        )}
      </button>
    </form>
  );
}
